import { ComponentType, ReactNode, useContext, type JSX } from 'react';

/** Matches Assets'/Fonts' own (narrower-than-ReactNode) children prop type. */
type WrapperChildren = string | JSX.Element | JSX.Element[];
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider, type AnyRouter } from '@tanstack/react-router';

import { useInterval } from '@/app.hooks';
import { AppContext } from '@/app.context';
import { useAppInstallEvent } from '@shared/events';
import { SkipLink } from '@shared/ui/skip-link';
import { Assets } from './fonts/assets';

/**
 * Shared "core app" shell (improvements/07-template-architecture.md's `app/`
 * layer, the composition half of "routes, auth, downloads, player... each
 * template only declares its differences"). Routing itself is
 * `createTenantApp` (routing/routeCatalog.tsx); this is the rest of what
 * every template's App.tsx duplicated around that router --
 * HelmetProvider/font-or-icon wrapper, install tracking, and the background
 * Cognito token-refresh loop.
 *
 * Downloads and the player aren't part of this yet -- neither exists as a
 * real feature in the app today (Section 20/PWA-features is still
 * NOT STARTED), so there's nothing to assemble for them. Extend this shell
 * when they land, rather than guessing their shape now.
 */
export interface RefreshConfig {
	tryRefreshToken: (redirect: string, offset: { minutes: number }) => void;
	intervalMs: number;
	/**
	 * daily/starmagazine/toyota skip the refresh call while offline (they
	 * gate on `useIsOnline()` themselves and pass the current value here).
	 * Re-evaluated every render, same as the interval's own callback ref --
	 * omit for templates that always refresh regardless of connectivity.
	 */
	enabled?: boolean;
}

export interface TenantAppShellProps {
	router: AnyRouter;
	/** Extra classes appended after the base `'App'` class, e.g. `['app-magazine']` or a CSS-module class. */
	extraClassNames?: string[];
	/**
	 * Mounts `useAppInstallEvent()`. Default `true` -- `magazine`/`magshop`
	 * pass `false`, matching their pre-existing behavior (neither called it
	 * before this shell existed; not something to silently turn on for them).
	 */
	tracking?: boolean;
	/**
	 * Wraps the tree below `HelmetProvider` (font/icon loading). Defaults to
	 * the shared `Assets` component; `magshop` passes its own `Fonts`
	 * wrapper instead (it has never used `Assets`).
	 */
	AssetsWrapper?: ComponentType<{ children: WrapperChildren }>;
	/**
	 * Background token-refresh loop. Omit for templates with no Cognito/
	 * token background refresh at all (`morningstar`, `poets`, `sections`,
	 * `padi`).
	 */
	refresh?: RefreshConfig;
	/**
	 * Extra content rendered inside `HelmetProvider`, before the app div --
	 * e.g. `daily`/`starmagazine`/`toyota`'s online-status theme-color meta
	 * tag (`<Helmet>...</Helmet>`, computed by the template itself since it
	 * owns the `useIsOnline()` call already needed for `refresh.enabled`).
	 */
	head?: ReactNode;
}

function TrackingInstallEffect() {
	const { isInstalled } = useContext(AppContext);
	useAppInstallEvent(isInstalled);
	return null;
}

function BackgroundRefresh({ tryRefreshToken, intervalMs, enabled = true }: RefreshConfig) {
	useInterval(() => {
		if (!enabled) return;
		tryRefreshToken('/', { minutes: 1 });
	}, intervalMs);
	return null;
}

export function TenantAppShell({
	router,
	extraClassNames = [],
	tracking = true,
	AssetsWrapper = Assets,
	refresh,
	head,
}: TenantAppShellProps) {
	const classNames = ['App', ...extraClassNames];

	return (
		<AssetsWrapper>
			<HelmetProvider>
				{head}
				{tracking && <TrackingInstallEffect />}
				{refresh && <BackgroundRefresh {...refresh} />}
				<div className={classNames.join(' ')}>
					{/* First in the tab order, so a keyboard user can jump past
					    the topbar to the page's content. Each route marks its
					    own container with `MAIN_CONTENT_ID`. */}
					<SkipLink />
					<RouterProvider router={router} />
				</div>
			</HelmetProvider>
		</AssetsWrapper>
	);
}
